import currency from "currency.js";
import { useContext, useMemo } from "react";
import { Helmet, HelmetData } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { InstrumentTypeResponse } from "./codegen-api";
import {
  getAppName,
  getAppShortName,
  getAppDescription,
  getAppKeywords,
  getAppLinks,
  getBrandFaviconUrl,
  getBrandOgImageUrl,
} from "./config/chains";
import { MarketContext } from "./contexts/MarketContext";
import { MarketInstrumentContext } from "./contexts/MarketInstrumentContext";
import { PageEndpointEnum } from "./enums/endpoint";
import { pwaURLPrefix, usePWAFlow } from "./hooks/usePWAFlow";
import useMarkPriceWSS from "./hooks/wss/useMarkPriceWSS";
import defaultManifest from "./manifest";
import { startCase } from "./utils/strings";

const helmetData = new HelmetData({});

function Meta() {
  const { market } = useContext(MarketContext);
  const { getMarketPrecision } = useContext(MarketInstrumentContext);

  const { pwaInfo } = usePWAFlow();
  const location = useLocation();

  const currentPage: PageEndpointEnum = useMemo(() => {
    const { pathname } = location;
    const allPages = Object.keys(PageEndpointEnum);

    const matchedRouteKeys: string[] = allPages.filter((k) => {
      const route = PageEndpointEnum[k as keyof typeof PageEndpointEnum];

      const routePath = route.split("/")?.[1];
      const firstPathName = pathname.split("/")?.[1];

      return routePath === firstPathName;
    });

    // Get the last matched route
    if (matchedRouteKeys.length) {
      const lastMatchedKey = matchedRouteKeys[matchedRouteKeys.length - 1];
      return PageEndpointEnum[lastMatchedKey as keyof typeof PageEndpointEnum];
    }
    return PageEndpointEnum.TRADING;
  }, [location]);

  // Dynamic manifest url to allow redirecting to app
  const manifestUrl = useMemo(() => {
    // getting web manifest from constants and modifying values
    const myDynamicManifest = defaultManifest;
    const startUrl = getAppLinks().home;

    // modify icons to use absolute path
    const icons = myDynamicManifest.icons.map((v) => ({
      ...v,
      src: `${startUrl}${v.src}`,
    }));
    myDynamicManifest.icons = icons;

    // for PWA
    const pwaParams = pwaInfo ? `${pwaURLPrefix}${pwaInfo?.query}` : "";
    myDynamicManifest.start_url = `${startUrl}${pwaParams}`;
    myDynamicManifest.scope = startUrl;

    const stringManifest = JSON.stringify(myDynamicManifest);
    const blob = new Blob([stringManifest], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    return url;
  }, [pwaInfo]);

  const uniqueTickers = useMemo(
    () => ({
      [`${market.asset}-PERP`]: {
        asset: market.asset,
        derivative: InstrumentTypeResponse.Perpetual,
      },
    }),
    [market.asset]
  );

  const { instrumentMark } = useMarkPriceWSS(uniqueTickers);
  const mark = instrumentMark[`${market.asset}-PERP`];

  const tags = {
    title: getAppShortName(),
    description: getAppDescription(),
    keywords: getAppKeywords().join(", "),
    titleTemplate: "Home",
  };

  switch (currentPage) {
    case PageEndpointEnum.PWA:
    case PageEndpointEnum.TRADING: {
      if (mark?.mark_price) {
        tags.titleTemplate = `${currency(mark?.mark_price, {
          precision: getMarketPrecision(market.asset, market.derivative)
            .price_precision,
        }).format()} ${market.asset}-USD`;
      }
      break;
    }
    case PageEndpointEnum.PORTFOLIO:
      tags.titleTemplate = "Portfolio";
      break;
    case PageEndpointEnum.SETTINGS:
      tags.titleTemplate = "Settings";
      break;
    default: {
      const cleaned = currentPage.replace(/[-/]/g, " ");
      tags.titleTemplate = startCase(cleaned);
      break;
    }
  }

  tags.titleTemplate = `${tags.titleTemplate} • %s`;

  const fullAppName = getAppName();
  const links = getAppLinks();
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : links.home || "";
  const canonicalUrl = `${origin}${location.pathname}${location.search || ""}`;
  const ogImageUrl = `${origin}${getBrandOgImageUrl()}`;
  const ogImageFallback = `${origin}${getBrandFaviconUrl()}`;

  return (
    <Helmet
      helmetData={helmetData}
      titleTemplate={tags.titleTemplate}
      defer={false}
    >
      {/* Dynamically generated manifest with brand-aware URLs / PWA params */}
      <link rel="manifest" href={manifestUrl} />

      {/* Favicons (brand-aware, override the static HTML fallbacks) */}
      <link rel="icon" type="image/svg+xml" href={getBrandFaviconUrl({ svg: true })} />
      <link rel="icon" type="image/png" href={getBrandFaviconUrl()} />
      <link rel="apple-touch-icon" href={getBrandFaviconUrl()} />

      {/* Canonical updates per route so crawlers index distinct pages */}
      <link rel="canonical" href={canonicalUrl} />

      <title>{tags.title}</title>
      <meta name="description" content={tags.description} />
      <meta name="keywords" content={tags.keywords} />
      <meta name="application-name" content={tags.title} />
      <meta name="apple-mobile-web-app-title" content={tags.title} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={fullAppName} />
      <meta property="og:title" content={tags.title} />
      <meta property="og:description" content={tags.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:secure_url" content={ogImageUrl} />
      <meta property="og:image:alt" content={fullAppName} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={tags.title} />
      <meta name="twitter:description" content={tags.description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={fullAppName} />

      {/* Backup image reference for consumers that fail to load og-image.png */}
      <link rel="image_src" href={ogImageFallback} />
    </Helmet>
  );
}

export default Meta;
